import { UserMessage } from '@/bt-common/schemas/bt/user-message';
import { BtSlimChat } from '@/bt-common/schemas/wa/chat';
import { WaContact } from '@/common/models/waContact';
import { getWaChatContactDisplayName } from '@/common/utils/display-name-utils';
import { formatWaIdAsInternationalPhone } from '@/common/utils/phone-validation-utils';
import { WA_MEDIA_MESSAGE_TYPES } from '@/bt-common/wa-media-message-types';

export function isMediaMessageType(type: string): boolean {
    return WA_MEDIA_MESSAGE_TYPES.has(type);
}

/** A cached chat: the canonical BtSlimChat plus the entity-adapter key fields. */
export type NormalizedChat = BtSlimChat & { _id: string; _serialized: string };

/**
 * Normalize a server chat for entity-cache storage by deriving the adapter key.
 *
 * The param is typed `BtSlimChat` ON PURPOSE: the contract guarantees `chatId` is the
 * bare JID string (BtSlimChatSchema.chatId = z.string()), so any attempt to read the
 * old WhatsApp-internal `chat.id._serialized` object shape is now a COMPILE error.
 * That exact mismatch previously slipped through (param was `any`) and produced
 * `_id: undefined` for every chat — the entity adapter (selectId: item._id) then
 * keyed them all to `null`, collapsing the whole Team Inbox list into one entry.
 */
/** A message's author jid: the read row carries a jid string, a live
 *  listener message a wid object. */
export const authorIdOf = (message: any): string | undefined =>
    typeof message?.author === 'string' ? message.author : message?.author?._serialized;

export function normalizeChat(chat: BtSlimChat): NormalizedChat {
    return { ...chat, _id: chat.chatId, _serialized: chat.chatId };
}

/**
 * Team Inbox presentation rule: does this message leave the chat list alone
 * (no reorder, never the preview)? Over the CANONICAL types only (plan A.9.1
 * categories). A different question from bt-common isSystemMessageType ("no
 * customer-authored content"): a missed call (`call_log`) and a deleted
 * message (`revoked`) DO reorder and show as the preview, as in WhatsApp.
 *
 * These are often the chat's latest message object, so every path that seeds a
 * chat's `lastMessage`/sort time (NotifierService live events,
 * requestChatByIdThunk re-fetch) funnels through this guard.
 */
const NON_REORDERING_TYPES: readonly string[] = ['gp2', 'notification', 'ciphertext', 'group_invite'];

export const isSystemNotificationMessage = (msg: any): boolean =>
    !!msg && (
        NON_REORDERING_TYPES.includes(msg.type)
        || msg.subtype === 'description'
        // An empty-body notification_template is a notice; a template WITH a
        // body is a real message and reorders. (WhatsApp Business `biz_*`
        // notices arrive as `notification` — the engine maps them.)
        || (msg.type === 'notification_template' && !((msg.body || '') as string).trim())
    );

// Type definitions
export type ContactMinimal = {
    contactId: string;
    name?: string;
    pushname?: string;
};

// Shared international formatter (single home: phone-validation-utils).
export const formatPhoneNumber = formatWaIdAsInternationalPhone;

export const getDeletionMessage = (
    deletedBy: string,
    waSessionPhoneNumber: string,
    messageAuthorId: string,
    adminName: string,
): string => {
    const isCurrentUserMessageAuthor = isCurrentUserPhone(waSessionPhoneNumber, messageAuthorId);
    const isDeletedByAdmin = deletedBy === 'admin';
    const isDeletedBySender = deletedBy === 'sender';

    if (isDeletedByAdmin) { // relevant for group chats
        if (isCurrentUserMessageAuthor) {
            return `This message was deleted by ${adminName ? 'admin ' + adminName : 'admin'}`; //user is author- message was deleted by admin
        } else {
            return `You deleted this message as admin`; //user is not author- message was deleted by the user
        }
    }

    if (isDeletedBySender) {
        if (isCurrentUserMessageAuthor) {
            return 'You deleted this message'; //user is author- message was deleted by the user
        } else {
            return 'This message was deleted'; //user is not author- message was deleted by the message author
        }
    }

    return 'This message was deleted';
};

/** Coerce any time value (unix seconds, unix ms, or an ISO string) to unix SECONDS; 0 when unusable. */
const toUnixSeconds = (v: any): number => {
    if (v == null) return 0;
    if (typeof v === 'number') {
        if (!Number.isFinite(v) || v <= 0) return 0;
        return v > 1e12 ? Math.floor(v / 1000) : v;   // ms → s
    }
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n > 1e12 ? Math.floor(n / 1000) : n;
    const ms = Date.parse(v);                          // ISO string
    return Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
};

/**
 * Canonical chat sort/display time, in unix SECONDS.
 *
 * The list-query chat shape (BtSlimChat) carries `t` — NOT `timestamp`. The
 * live path (NotifierService / requestChatByIdThunk / optimistic send) is the
 * ONLY writer of `timestamp`, and it writes the raw WAWeb value which may be
 * undefined, a millisecond number, or even an ISO string — never a guaranteed
 * match for the fetched seconds scale. Reading `.timestamp` first (and raw)
 * therefore both (a) left most chats at 0 → blank times + a sort that only
 * *looked* right by the luck of an all-zero stable no-op, and (b) let one live
 * update lift a single chat onto a different/NaN scale and scramble the order.
 *
 * Fix: prefer `lastMessage.t`, which is ALWAYS unix-seconds AND is refreshed on
 * every live event (NotifierService sets `lastMessage: msg`) — so it reorders
 * correctly as messages arrive — then fall back to the fetched `t`, then coerce
 * whatever `timestamp` holds. Every field is funnelled through `toUnixSeconds`
 * so a mixed unit or an ISO string can never reorder the list.
 *
 * SYSTEM-NOTIFICATION EXCEPTION (#chat-order-system-notif): when the chat's
 * lastMessage is an encryption / group-membership / protocol notice, do NOT
 * sort by its time. WhatsApp itself never bumps a chat for these — verified
 * against the WA store, where such a chat carries `Chat.t == null` and sits low
 * in the list (`initialIndex` near the bottom) even though the e2e_notification
 * is its newest message object. Skipping `lastMessage.t` here lets the chat
 * fall back to its own `t`/`timestamp` (its real activity time), so a stray
 * security-code notice can't lift an old chat to the top of the Team Inbox.
 */
export const getChatTimestamp = (chat: any): number =>
    (isSystemNotificationMessage(chat?.lastMessage) ? 0 : toUnixSeconds(chat?.lastMessage?.t))
    || toUnixSeconds(chat?.t)
    || toUnixSeconds(chat?.timestamp)
    || (isSystemNotificationMessage(chat?.lastMessage) ? 0 : toUnixSeconds(chat?.lastMessage?.timestamp))
    || 0;

export const isCurrentUserPhone = (
        waSessionPhoneNumber: string,
    serializedPhoneNumber: string,
): boolean => {
    if (!waSessionPhoneNumber || !serializedPhoneNumber) return false;

    const phoneNumber = serializedPhoneNumber.split('@')[0];
    return phoneNumber === waSessionPhoneNumber;
};



export const formatDuration = (durationInSeconds: number | string) => {
    // Convert the input to a number
    // const seconds = parseInt(durationInSeconds, 10); // Using base 10 for conversion
    const seconds = Number(durationInSeconds);

    // Check if the conversion was successful
    if (isNaN(seconds) || seconds === 0) {
        return "0:00"; // Return a default value if conversion fails
    }

    // If duration is more than an hour
    if (seconds >= 3600) {
        const hours = Math.floor(seconds / 3600);
        return `${hours}h`; // Format as (number of hours)h
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`; // Format as m:ss
};


export interface ContactIdExtractorInput {
    author?: string;
    type?: string;
    subtype?: string;
    revokeSender?: string;
    /** Declared rows carry jid strings; live listener events (backend pass-through) carry `{_serialized}`. */
    recipients?: Array<string | { _serialized?: string }>;
}

export const extractContactIdsFromMessage = ({ author, type, subtype, revokeSender, recipients }: ContactIdExtractorInput): string[] => {
    const contactIds: string[] = [];

    // Add author if available
    if (author) {
        contactIds.push(author);
    }

    // Add revoke sender if available
    if (type === 'revoked' &&
        subtype === 'admin' &&
        revokeSender) {
        contactIds.push(revokeSender);
    }

    // Add recipients if available
    if (type === 'gp2' && Array.isArray(recipients)) {
        recipients.forEach(contact => {
            const id = typeof contact === 'string' ? contact : contact?._serialized;
            if (id) contactIds.push(id);
        });
    }

    return contactIds;
};

const formatParticipantNames = (recipients: Array<string | { _serialized?: string }> | undefined, waSessionPhoneNumber: string, displayNames: Record<string, string>) => {
    if (!Array.isArray(recipients) || recipients.length === 0) {
        return "Group Participant";
    }

    const names = recipients.map(contact => {
        const contactId = typeof contact === 'string' ? contact : contact?._serialized;
        if (!contactId) return "Unknown";

        return isCurrentUserPhone(waSessionPhoneNumber, contactId)
            ? "You"
            : displayNames[contactId] || formatPhoneNumber(contactId) || "Unknown";
    }).filter(Boolean); // remove any undefined/null results


    if (names.length === 0) return "Unknown Participant";
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(" & ");

    const lastName = names.pop();
    return `${names.join(", ")} & ${lastName}`;
};

export function getMessageText(
    message: any,
    displayNames: Record<string, string>,
    waSessionPhoneNumber: string,
): string {
    if (!message) return '';

    const isViewOnce = message?.isViewOnce;

    switch (message.type) {
        case "chat":
            return message.body;

        case "audio":
            // Show the clip length when the projection carries it, else a
            // label — never a bare "0:00" (WhatsApp shows the duration).
            return message.duration > 0 ? formatDuration(message.duration) : "Audio";

        case "ptt":
            return message.duration > 0 ? formatDuration(message.duration) : "Voice message";

        case "image":
            return isViewOnce ? "Photo" : message.caption || "Photo";

        case "video":
            return isViewOnce ? "Video" : message.caption || "Video";

        case "document":
            return message.caption || message.filename || "Document";

        case "sticker":
            return "Sticker";

        case "location":
            if (message.isLive) {
                return message.comment || "Live location";
            } else {
                return "Location";
            }

        case "vcard":
            return message.vcardFormattedName || "Contact";

        case "multi_vcard":
            const vcardList = message.vcardList;

            // Sort vcardList alphabetically by displayName, ignoring case
            const sortedVcardList = [...vcardList].sort((a, b) => a.displayName.localeCompare(b.displayName));

            // Find the first available displayName in the sorted list
            const firstAvailableDisplayName = sortedVcardList.find(vcard => vcard.displayName)?.displayName || "Unknown Contact";

            const additionalContacts = vcardList.length - 1; // Calculate the remaining contacts

            return `${firstAvailableDisplayName}${additionalContacts > 0 ? ` and ${additionalContacts} other contact${additionalContacts > 1 ? 's' : ''}` : ''}`;

        case "revoked":

            let revokeSender = 'user';
            const isSentByMe = message.fromMe;

            if (isSentByMe && message.subtype === 'admin') {
                const phoneNumber = message.revokeSender?._serialized;
                if (phoneNumber && displayNames) {
                    revokeSender = displayNames[phoneNumber];
                }
            }
            return getDeletionMessage(
                message.subtype,
                waSessionPhoneNumber,
                authorIdOf(message) ?? '',
                revokeSender);

        case "ciphertext":
            switch (message.subtype) {
                case "view_once_unavailable_fanout":
                    return "Message";

                default:
                    return "Encrypted message. For added privacy, you can only open it on your phone.";
            }

        case "call_log": {
            // WAWeb encodes calls as call_log with subtypes like
            // 'miss' / 'miss_video' / 'outgoing' / 'outgoing_video' / 'incoming'.
            const isVideo = (message.subtype || '').includes('video');
            const kind = isVideo ? 'video call' : 'voice call';
            return (message.subtype || '').startsWith('miss')
                ? `Missed ${kind}`
                : kind.charAt(0).toUpperCase() + kind.slice(1);
        }

        case "poll_creation":
            // Slim projection surfaces the poll question as body (and in
            // message.poll.question); show the question like WA Web does.
            return message.poll?.question || message.pollName || message.body || "Poll";

        case "gp2":

            let participantNames = 'group participant';

            if (message.recipients) {
                participantNames = formatParticipantNames(message.recipients, waSessionPhoneNumber, displayNames);
            }

            const authorId = authorIdOf(message);
            const authorString =
                (authorId && (displayNames?.[authorId] || formatPhoneNumber(authorId))) ||
                'Group member';


            switch (message.subtype) {

                case 'add': //GROUP_JOIN
                    return `${authorString} added ${participantNames}`;

                case 'invite': //GROUP_JOIN
                    return `${participantNames} joined via an invite link`;

                case 'remove': //GROUP_LEAVE
                    return `${authorString} removed ${participantNames}`;

                case 'leave': //GROUP_LEAVE
                    return `${participantNames} left`;

                case 'promote': //GROUP_ADMIN_CHANGED
                    return "You're now an admin";

                case 'demote': //GROUP_ADMIN_CHANGED
                    return "You're no longer an admin";

                case 'subject': //GROUP_UPDATE
                    return `${authorString} changed the group name to "${message?.body}"`;

                case 'description': //GROUP_UPDATE
                    return `${authorString} changed the group description.`;

                case 'picture': //GROUP_UPDATE
                    return `${authorString} ${message?.body === "remove" ? "deleted" : "changed"} this group's icon`;

                case 'membership_approval_mode': //GROUP_MEMBERSHIP_REQUEST
                    return `${authorString} turned ${message.templateParams[0]} admin approval to join this group. Click to change.`;

                case 'member_add_mode': //GROUP_UPDATE
                    return `${authorString}  changed this group's setting to allow ${message.templateParams[0] === "on" ? "all members" : "only admins"} to add others to this group. Click to change.`;

                case 'announce': //GROUP_UPDATE
                    return `${authorString} changed this group's settings to allow ${message.templateParams[0] === "on" ? "only admins" : "all members"} to send messages to this group`;

                case 'restrict': //GROUP_UPDATE
                    return `${authorString} changed the settings ${message.templateParams[0] === "on" ? "only admins" : "all members"} can edit the group settings`;

                default:
                    return "Group notification";
            }

        // Pure system / security / protocol events carry no human-readable
        // content — WhatsApp shows NO preview text for these (blank row), so
        // don't surface a scary "Unsupported message type".
        case "notification":
        case "group_invite":
            return '';

        default:
            // Everything else (notification_template, unknown, or a
            // message with no `type`): show whatever text it actually carries,
            // else nothing. Never the literal "Unsupported message type".
            return message.body || message.caption || '';
    }
}

export function formatMessageBody(msg: any, waSessionPhoneNumber: string, displayNames: Record<string, string>): string {
    if (!msg) return "";

    const { type, subtype, isViewOnce, caption, comment, filename } = msg;
    // Infer hasMedia from type - use || because WhatsApp API returns hasMedia: false for paginated messages
    const hasMedia = msg.hasAttachedMedia || msg.hasMedia || isMediaMessageType(type);

    let fallBackBody: string | undefined;

    if (type === "ciphertext") {
        if (subtype === "view_once_unavailable_fanout") {
            fallBackBody = "You received a view once message. For added privacy, you can only open it on your phone.";
        } else {
            fallBackBody = "Encrypted message. For added privacy, you can only open it on your phone.";
        }

    } else if (isViewOnce) {
        const mediaType = getMessageText(msg, displayNames, waSessionPhoneNumber);
        fallBackBody = `View-once ${mediaType}. Currently not supported — open on your phone to view.`;

    } else if (hasMedia) {
        if (type === "document" && caption === filename) {
            fallBackBody = "";
        } else if (type === "audio" || type === "ptt") {
            // WhatsApp Web never renders caption text on audio/voice bubbles
            // (API-sent audio can still carry one on the model) — show only
            // the player, like the official UI.
            fallBackBody = "";
        } else {
            fallBackBody = caption || comment || "";
        }
    }

    return fallBackBody ?? getMessageText(msg, displayNames, waSessionPhoneNumber);
}

/** Best-effort MIME for a backup/vault media row that carries no mimetype of its
 *  own. The attachment preview picks its renderer off `asset.type` (must start
 *  with image/ · video/ · audio/ · application/pdf); the real bytes load from the
 *  URL regardless of this value. Derived from the WA message TYPE (+ the filename
 *  extension for documents). Without it, a backed-up image/video/doc shows a
 *  blank bubble even though its full-res URL is set. */
export const mimeForMessageType = (type?: string, filename?: string | null): string | undefined => {
    switch (type) {
        case 'image': return 'image/jpeg';
        case 'sticker': return 'image/webp';
        case 'video':
        case 'gif': return 'video/mp4';
        case 'ptt':
        case 'audio': return 'audio/ogg';
        case 'document': {
            const ext = (filename ?? '').split('.').pop()?.toLowerCase() ?? '';
            if (ext === 'pdf') return 'application/pdf';
            if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
            if (['mp4', 'mov', 'webm'].includes(ext)) return 'video/mp4';
            if (['mp3', 'm4a', 'ogg', 'wav'].includes(ext)) return `audio/${ext}`;
            return 'application/octet-stream';
        }
        default: return undefined;
    }
};

/** Which backup media-category a WA message type falls under (null = not
 *  bucket media). Mirrors the engine's `mediaCategoryForType`. */
const BACKUP_CATEGORY_FOR_TYPE: Record<string, 'images' | 'videos' | 'documents' | 'audio'> = {
    image: 'images', sticker: 'images',
    video: 'videos', gif: 'videos',
    document: 'documents',
    ptt: 'audio', audio: 'audio',
};
const BACKUP_CATEGORY_LABEL: Record<string, string> = {
    images: 'Image', videos: 'Video', documents: 'Document', audio: 'Audio',
};

/** Why a vault/DB-tier media message is showing a THUMBNAIL instead of the
 *  full-res backup. Returns a short human reason (for the bt-debug overlay) or
 *  null when there's nothing to explain (not media, or actually backed up).
 *  The most important case is a category the user turned OFF in Backup settings
 *  (videos are off by default) — that's why the file was never backed up. */
export const getBackupThumbnailReason = (
    type: string | undefined,
    mediaConfig: { images?: boolean; videos?: boolean; documents?: boolean; audio?: boolean } | undefined,
): string | null => {
    const cat = type ? BACKUP_CATEGORY_FOR_TYPE[type] : undefined;
    if (!cat) return null;
    // Defaults match the engine: images/documents/audio ON, videos OFF.
    const enabled = mediaConfig?.[cat] ?? (cat === 'videos' ? false : true);
    if (!enabled) return `${BACKUP_CATEGORY_LABEL[cat]} backup is OFF — enable “${BACKUP_CATEGORY_LABEL[cat]}s” in Backup settings`;
    return 'Not backed up yet — pending, or WhatsApp no longer has the original file';
};

/** AUTHORITATIVE end-user text for an engine-recorded media-skip CODE
 *  (`BackedUpMessage.mediaSkipReason`, persisted server-side). Unlike the
 *  heuristic `getBackupThumbnailReason` (a client GUESS from current config,
 *  bt-debug only), this reflects the reason the backup engine actually recorded
 *  when it processed the file — so it's shown to EVERY user, not just debuggers.
 *  `type` names the category for the `category-off` case. Returns null for an
 *  unknown/empty code (caller may fall back to the heuristic). */
export const reasonTextForMediaSkip = (
    reason: string | undefined,
    type: string | undefined,
): string | null => {
    if (!reason) return null;
    const cat = type ? BACKUP_CATEGORY_FOR_TYPE[type] : undefined;
    switch (reason) {
        case 'category-off':
            return cat
                ? `Not backed up — ${BACKUP_CATEGORY_LABEL[cat]} backup is turned off. Enable “${BACKUP_CATEGORY_LABEL[cat]}s” in Backup settings.`
                : 'Not backed up — this media type is turned off in Backup settings.';
        case 'too-large':
            return 'Not backed up — the file is over your backup size limit.';
        case 'unavailable':
            return 'Not backed up — WhatsApp no longer has the original file.';
        default:
            return null;
    }
};

export function mapUserMessage(currentMessage: any, waSessionPhoneNumber: string, displayNames: Record<string, string>): UserMessage | null {
    if (!currentMessage) return null;

    // Infer hasMedia from type - use || because WhatsApp API returns hasMedia: false for paginated messages
    const hasMedia = currentMessage.hasMedia || isMediaMessageType(currentMessage.type);
    const canShowAsset = hasMedia && currentMessage.type !== 'revoked' && !currentMessage.isViewOnce;

    // Use mediaData from waGetMessageMediaTool (full) if available, otherwise fall back to body
    const mediaData = currentMessage.mediaData;
    const assetUrl = mediaData?.url || mediaData?.mediaUrl || currentMessage.body || '';
    const assetMimetype = mediaData?.mimetype || currentMessage.mimetype;
    const assetFilename = mediaData?.filename || currentMessage.filename;
    // GIF flag lives on the projected message (createLeanMessage) or on the
    // fetched mediaData; either identifies a WhatsApp GIF the UI autoplays.
    const assetIsGif = currentMessage.isGif === true || mediaData?.isGif === true;

    return {
        _id: currentMessage.waMessageKey?._serialized ?? currentMessage.id?._serialized,
        message: formatMessageBody(currentMessage, waSessionPhoneNumber, displayNames),
        asset: canShowAsset
            ? {
                name: assetFilename,
                type: assetMimetype,
                url: assetUrl,
                isGif: assetIsGif,
            }
            : undefined,
        dueDate: new Date(currentMessage.timestamp * 1000),
        waMessageKey: {
            fromMe: currentMessage.fromMe,
        },
        ack: currentMessage.ack,
    };
}

// Generate consistent color based on sender name
export const getSenderColor = (name: string): string => {
    if (!name) return '';

    // Simple hash function to convert string to number
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        const char = name.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }

    // Use hash to generate a color
    const hue = Math.abs(hash) % 360;
    const saturation = 60 + (Math.abs(hash) % 20); // 60-80%
    const lightness = 45 + (Math.abs(hash) % 15); // 45-60%

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};


export function getDisplayName(
    contact: Partial<WaContact>,
    waSessionPhoneNumber: string,
): string {

    if (isCurrentUserPhone(waSessionPhoneNumber, contact._serialized ?? '')) return 'You';

    // Use unified display name utility with pushname prefix and phone number formatting
    return getWaChatContactDisplayName(contact, {
        prefixPushname: true,
        fallback: formatPhoneNumber(contact._serialized ?? '') || 'contact not found'
    });
}

export function getDisplayNamesMap(
    contactIds: string[],
    contactsMap: Record<string, WaContact | undefined>,
    waSessionPhoneNumber: string
): Record<string, string> {
    return contactIds.reduce((result, id) => {
        const contact = contactsMap[id];

        const displayName = getDisplayName(
            {
                _serialized: id,
                name: contact?.name,
                pushname: contact?.pushname
            },
            waSessionPhoneNumber
        );

        result[id] = displayName;
        return result;
    }, {} as Record<string, string>);
}

// ===========================================================================
// WhatsApp-parity message-area helpers (pure, unit-testable)
// ===========================================================================

/**
 * The short WhatsApp message id (the stanza id) — the participant/remote-agnostic
 * part of a message key. Two representations of the SAME message can differ in
 * their `_serialized` form (e.g. an @lid vs @c.us participant / remote), but the
 * short `id` is stable, so all cross-message matching (reply→original, and
 * optimistic-send vs live-event dedupe) keys off it.
 *
 * The serialized-split fallback is load-bearing: a LIVE-inserted own message is
 * the raw WAWeb shape — no projected `waMessageKey`, and `id` is just
 * `{ _serialized }` with no short `.id`. Without the split, this returned
 * `undefined` for that entity, so the optimistic-send dedupe scan (which matches
 * candidates via this helper) never found it and inserted a SECOND bubble until
 * a refresh. The 3rd `_`-segment of a serialized key is the stanza id
 * (`<fromMe>_<chatJid>_<stanzaId>[_<participant>]`); fromMe and the chat JID
 * never contain underscores, so segment[2] is always the short id.
 */
export function getMessageShortId(msg: any): string | undefined {
    return (
        msg?.waMessageKey?.id
        ?? msg?.id?.id
        ?? (msg?.waMessageKey?._serialized ?? msg?.id?._serialized ?? msg?._id ?? '').split('_')[2]
        ?? undefined
    ) || undefined;
}

/** Short id of a quoted-message reference (new `quotedMessage` shape). */
export function getQuotedShortId(quoted: any): string | undefined {
    if (!quoted) return undefined;
    return quoted?.waMessageKey?.id ?? undefined;
}

/**
 * True when `msg` is the original that `quoted` points at. Matches on the short
 * stanza id only (see getMessageShortId) so it works across @lid/@c.us drift.
 * Also accepts the legacy `quotedStanzaID` string fallback.
 */
export function messageMatchesQuoted(msg: any, quoted: any, legacyStanzaId?: string): boolean {
    const target = getQuotedShortId(quoted) ?? legacyStanzaId;
    if (!target) return false;
    const shortId = getMessageShortId(msg);
    return !!shortId && shortId === target;
}

export interface RawReaction {
    text: string;
    from?: string;
    fromMe?: boolean;
    t?: number;
}

export interface AggregatedReaction {
    emoji: string;
    count: number;
    fromMe: boolean;
    froms: string[];
}

export interface AggregatedReactions {
    groups: AggregatedReaction[]; // distinct emojis, first-seen order
    total: number; // total reaction count across all emojis
    myReaction?: string; // the emoji this user reacted with (fromMe), if any
}

/**
 * Aggregate a message's raw reaction list into WhatsApp's display shape: one
 * entry per distinct emoji (in first-seen order), a total count, and the current
 * user's own reaction. Empty-text entries are dropped (an empty reaction text is
 * WhatsApp's "reaction removed" sentinel).
 */
export function aggregateReactions(reactions?: RawReaction[] | null): AggregatedReactions {
    const empty: AggregatedReactions = { groups: [], total: 0, myReaction: undefined };
    if (!Array.isArray(reactions) || reactions.length === 0) return empty;

    const order: string[] = [];
    const byEmoji = new Map<string, AggregatedReaction>();
    let myReaction: string | undefined;

    for (const r of reactions) {
        const emoji = (r?.text ?? '').trim();
        if (!emoji) continue; // removed reaction
        let group = byEmoji.get(emoji);
        if (!group) {
            group = { emoji, count: 0, fromMe: false, froms: [] };
            byEmoji.set(emoji, group);
            order.push(emoji);
        }
        group.count += 1;
        if (r.fromMe) {
            group.fromMe = true;
            myReaction = emoji;
        }
        if (r.from) group.froms.push(r.from);
    }

    const groups = order.map(e => byEmoji.get(e)!);
    const total = groups.reduce((sum, g) => sum + g.count, 0);
    return { groups, total, myReaction };
}

/**
 * Deterministic pseudo-waveform bar heights for a voice message. Seeded from the
 * message id (xorshift over a string hash) so the same message always renders
 * the same waveform across re-renders/remounts, without real audio analysis.
 */
export function generateWaveformHeights(
    seed: string,
    barCount = 30,
    minHeight = 4,
    maxHeight = 19,
): number[] {
    // String → 32-bit hash (same technique as getSenderColor).
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i);
        hash = hash & hash;
    }
    let x = hash || 0x9e3779b9;

    const range = maxHeight - minHeight;
    const heights: number[] = [];
    for (let i = 0; i < barCount; i++) {
        // xorshift32 — cheap deterministic PRNG.
        x ^= x << 13; x = x & x;
        x ^= x >>> 17;
        x ^= x << 5; x = x & x;
        const unit = (Math.abs(x) % 1000) / 1000;
        heights.push(Math.round(minHeight + unit * range));
    }
    return heights;
}

/** Short human label for a non-text quoted/preview message type (WhatsApp-style).
 *  Returns undefined for plain text so callers show the actual body instead. */
export function mediaTypeLabel(type: string | undefined): string | undefined {
    switch (type) {
        case 'image': return 'Photo';
        case 'video': return 'Video';
        case 'audio': return 'Audio';
        case 'ptt': return 'Voice message';
        case 'document': return 'Document';
        case 'sticker': return 'Sticker';
        case 'gif': return 'GIF';
        case 'location': return 'Location';
        case 'vcard':
        case 'multi_vcard': return 'Contact';
        case 'poll_creation': return 'Poll';
        default: return undefined;
    }
}

export interface QuotedInfo {
    /** short stanza id of the original, for scroll-to-original matching */
    shortId?: string;
    /** whether the quoted original was sent by us (drives the 'You' author label) */
    fromMe: boolean;
    /** serialized author id of the quoted original (group sender), if known */
    authorId?: string;
    type: string;
    /** text snippet to display (empty for pure media) */
    text: string;
    /** media label ('Photo', 'Voice message', ...) when the original is non-text */
    mediaLabel?: string;
}

/**
 * Normalize a reply's quote reference into one display shape. Prefers the new
 * `quotedMessage` field; falls back to the legacy `quotedMsg` + `quotedStanzaID`
 * pair that older builds attached. Returns null when the message is not a reply.
 */
export function getQuotedInfo(message: any): QuotedInfo | null {
    const q = message?.quotedMessage;
    if (q && (q.waMessageKey || q.text != null || q.type)) {
        const type = q.type || 'chat';
        return {
            shortId: q.waMessageKey?.id,
            fromMe: !!q.waMessageKey?.fromMe,
            authorId: q.author ?? undefined,
            type,
            text: q.text ?? '',
            mediaLabel: mediaTypeLabel(type),
        };
    }

    const legacy = message?.quotedMsg;
    const stanza = message?.quotedStanzaID;
    if (legacy || stanza) {
        const type = legacy?.type || 'chat';
        return {
            shortId: typeof stanza === 'string' ? stanza : undefined,
            fromMe: !!legacy?.fromMe,
            authorId: legacy?.author?._serialized ?? legacy?.author ?? undefined,
            type,
            text: legacy?.body ?? legacy?.caption ?? '',
            mediaLabel: mediaTypeLabel(type),
        };
    }

    return null;
}

/**
 * Compute the optimistic next reaction list AND the text to send when the current
 * user taps `emoji` on a message. WhatsApp allows a single reaction per user:
 *  - tapping the emoji you already reacted with removes it (send empty text);
 *  - tapping a different emoji replaces your previous reaction.
 * Other people's reactions are preserved untouched.
 */
export function toggleMyReaction(
    reactions: RawReaction[] | undefined | null,
    emoji: string,
): { next: RawReaction[]; sendText: string } {
    const others = Array.isArray(reactions) ? reactions.filter(r => !r.fromMe) : [];
    const currentMine = Array.isArray(reactions)
        ? reactions.find(r => r.fromMe)?.text?.trim()
        : undefined;

    if (currentMine && currentMine === emoji.trim()) {
        return { next: others, sendText: '' };
    }
    return {
        next: [...others, { text: emoji, fromMe: true, t: Math.floor(Date.now() / 1000) }],
        sendText: emoji,
    };
}

/**
 * A stable per-sender key used for consecutive-message clustering. Own messages
 * all share 'me'; received messages cluster by author (group) or chat peer.
 */
export function messageSenderKey(msg: any): string {
    if (!msg) return '';
    if (msg.fromMe) return 'me';
    return (
        msg.author?._serialized ??
        msg.author ??
        msg.from ??
        msg.senderName ??
        msg.notifyName ??
        'peer'
    );
}

export interface ClusterFlags {
    /** First bubble of a run of same-sender consecutive messages (gets the tail). */
    isFirstOfCluster: boolean;
    /** Last bubble of the run (WA anchors the group avatar / extra gap here). */
    isLastOfCluster: boolean;
}

/**
 * Compute cluster flags for a message given its neighbours in the flattened
 * display list. A cluster breaks on sender change, day change, or a system
 * (gp2) message — mirroring how WhatsApp groups consecutive bubbles.
 */
export function computeClusterFlags(prev: any | null, current: any, next: any | null): ClusterFlags {
    const sameCluster = (a: any | null, b: any | null): boolean => {
        if (!a || !b) return false;
        if (a.type === 'gp2' || b.type === 'gp2') return false;
        if (messageSenderKey(a) !== messageSenderKey(b)) return false;
        return getDateKey(a.timestamp) === getDateKey(b.timestamp);
    };

    return {
        isFirstOfCluster: !sameCluster(prev, current),
        isLastOfCluster: !sameCluster(current, next),
    };
}

export function getDateKey(timestamp: number): string {
    const d = new Date(timestamp * 1000);
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${day}-${month}-${year}`;
}

export const timestampToDateLabel = (timestamp: number, showTime: boolean = true): string => {
    if (!timestamp) return "";

    const messageDate = new Date(timestamp * 1000);
    const now = new Date();

    // normalize both dates to midnight
    const normalize = (d: Date) => {
        const n = new Date(d);
        n.setHours(0, 0, 0, 0);
        return n.getTime();
    };

    const diffDays = Math.floor(
        (normalize(now) - normalize(messageDate)) / (1000 * 60 * 60 * 24)
    );

    // Today
    if (diffDays === 0) {
        return showTime
            ? messageDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
            : "Today";
    }

    // Yesterday
    if (diffDays === 1) {
        return "Yesterday";
    }

    // Last week
    if (diffDays <= 6) {
        return messageDate.toLocaleDateString([], { weekday: "long" });
    }

    // Other dates
    return `${messageDate.getDate()}/${messageDate.getMonth() + 1}/${messageDate.getFullYear().toString().slice(-2)}`;
}; 